import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Home, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * 隐私政策页面
 */
function PrivacyPolicy() {
  const { language } = useLanguage();

  const content = {
    zh: {
      title: '隐私政策',
      lastUpdated: '最后更新：2025年12月23日',
      sections: [
        {
          title: '1. 信息收集',
          content: `
            <p>DevToolkit 重视您的隐私。我们收集以下类型的信息：</p>
            <h3>1.1 自动收集的信息</h3>
            <ul>
              <li><strong>使用数据：</strong>通过 Google Analytics 收集页面浏览、访问时间、设备类型等匿名统计数据</li>
              <li><strong>技术信息：</strong>浏览器类型、操作系统、IP 地址（匿名化）</li>
              <li><strong>Cookies：</strong>用于保存用户偏好（主题、语言设置等）</li>
            </ul>
            <h3>1.2 用户提供的信息</h3>
            <ul>
              <li><strong>工具使用：</strong>您在工具中输入的数据（JSON、时间戳等）仅在浏览器本地处理，不会上传到服务器</li>
              <li><strong>历史记录：</strong>保存在浏览器本地存储，不会发送到服务器</li>
            </ul>
          `
        },
        {
          title: '2. 信息使用',
          content: `
            <p>我们使用收集的信息用于：</p>
            <ul>
              <li>改进网站功能和用户体验</li>
              <li>分析使用模式和趋势</li>
              <li>保存用户偏好设置（主题、语言）</li>
              <li>监控网站性能和错误</li>
            </ul>
            <p><strong>我们不会：</strong></p>
            <ul>
              <li>❌ 出售您的个人信息</li>
              <li>❌ 分享您的数据给第三方（除下述服务外）</li>
              <li>❌ 追踪您的个人身份</li>
            </ul>
          `
        },
        {
          title: '3. 第三方服务',
          content: `
            <p>我们使用以下第三方服务：</p>
            <h3>3.1 Google Analytics</h3>
            <ul>
              <li><strong>用途：</strong>网站访问统计分析</li>
              <li><strong>数据：</strong>页面浏览、用户来源、设备信息</li>
              <li><strong>隐私政策：</strong><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google 隐私政策</a></li>
              <li><strong>退出：</strong>您可以安装 <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics 退出插件</a></li>
            </ul>
            <h3>3.2 Google AdSense</h3>
            <ul>
              <li><strong>用途：</strong>在网站上展示广告</li>
              <li><strong>数据：</strong>Google 及其合作伙伴会在您的浏览器中放置和读取 Cookie，用于根据您的兴趣投放广告</li>
              <li><strong>Cookie 使用：</strong>
                <ul>
                  <li>用于投放个性化广告</li>
                  <li>衡量广告效果</li>
                  <li>防止广告欺诈</li>
                </ul>
              </li>
              <li><strong>隐私政策：</strong><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google 隐私政策</a></li>
              <li><strong>详细了解：</strong><a href="https://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener noreferrer">Google 如何使用合作伙伴网站的数据</a></li>
              <li><strong>管理广告偏好：</strong>访问 <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google 广告设置</a> 来自定义您的广告体验</li>
            </ul>
            <h3>3.3 Cloudflare</h3>
            <ul>
              <li><strong>用途：</strong>CDN 加速和安全防护</li>
              <li><strong>隐私政策：</strong><a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare 隐私政策</a></li>
            </ul>
          `
        },
        {
          title: '4. Cookies 和本地存储',
          content: `
            <p>我们使用 Cookies 和浏览器本地存储来：</p>
            <h3>4.1 必要 Cookies</h3>
            <ul>
              <li><strong>用户偏好：</strong>保存主题设置（亮色/暗色）、语言选择</li>
              <li><strong>历史记录：</strong>保存您的 JSON 输入历史（仅本地）</li>
            </ul>
            <h3>4.2 分析 Cookies</h3>
            <ul>
              <li><strong>Google Analytics：</strong>用于分析网站使用情况</li>
              <li><strong>数据收集：</strong>匿名的访问统计信息</li>
            </ul>
            <h3>4.3 广告 Cookies（第三方）</h3>
            <ul>
              <li><strong>Google AdSense：</strong>Google 及其广告合作伙伴使用 Cookies 来：
                <ul>
                  <li>在您的浏览器中放置和读取 Cookie</li>
                  <li>根据您过去对本网站和其他网站的访问记录来投放广告</li>
                  <li>衡量广告的有效性</li>
                </ul>
              </li>
              <li><strong>个性化广告：</strong>这些 Cookie 使 Google 及其合作伙伴能够根据您在本网站和/或互联网上其他网站上的访问记录向您展示广告</li>
            </ul>
            <h3>4.4 如何控制 Cookies</h3>
            <ul>
              <li><strong>浏览器设置：</strong>您可以在浏览器设置中禁用 Cookies</li>
              <li><strong>Google 广告设置：</strong>访问 <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google 广告设置</a> 停用个性化广告</li>
              <li><strong>退出广告 Cookies：</strong>访问 <a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">aboutads.info</a> 或 <a href="http://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer">youronlinechoices.eu</a></li>
              <li><strong>注意：</strong>禁用 Cookies 后某些功能可能无法正常工作（如保存偏好设置）</li>
            </ul>
          `
        },
        {
          title: '5. 数据安全',
          content: `
            <p>我们采取以下措施保护您的数据：</p>
            <ul>
              <li><strong>HTTPS 加密：</strong>所有数据传输使用 SSL/TLS 加密</li>
              <li><strong>本地处理：</strong>工具数据（JSON 等）完全在浏览器本地处理</li>
              <li><strong>不存储敏感数据：</strong>我们的服务器不存储您的工具使用数据</li>
              <li><strong>定期更新：</strong>保持软件和依赖项更新</li>
            </ul>
          `
        },
        {
          title: '6. 您的权利',
          content: `
            <p>根据相关法律法规，您拥有以下权利：</p>
            <ul>
              <li><strong>访问权：</strong>您可以清除浏览器 Cookies 和本地存储来删除所有本地数据</li>
              <li><strong>退出权：</strong>您可以禁用 Cookies 或使用广告拦截器退出追踪</li>
              <li><strong>数据可携带权：</strong>本地数据可以通过浏览器开发者工具导出</li>
            </ul>
          `
        },
        {
          title: '7. 儿童隐私',
          content: `
            <p>我们的服务不针对 13 岁以下的儿童。我们不会有意收集儿童的个人信息。如果您发现我们无意中收集了儿童信息，请联系我们删除。</p>
          `
        },
        {
          title: '8. 政策变更',
          content: `
            <p>我们可能会不时更新本隐私政策。重大变更时，我们会：</p>
            <ul>
              <li>在本页面顶部更新"最后更新"日期</li>
              <li>在网站上发布通知</li>
            </ul>
            <p>建议您定期查看本政策以了解最新信息。</p>
          `
        },
        {
          title: '9. 联系我们',
          content: `
            <p>如果您对本隐私政策有任何疑问或建议，请通过以下方式联系我们：</p>
            <ul>
              <li><strong>GitHub：</strong><a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">提交 Issue</a></li>
              <li><strong>网站：</strong><a href="/contact">联系页面</a></li>
            </ul>
          `
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: December 23, 2025',
      sections: [
        {
          title: '1. Information Collection',
          content: `
            <p>DevToolkit values your privacy. We collect the following types of information:</p>
            <h3>1.1 Automatically Collected Information</h3>
            <ul>
              <li><strong>Usage Data:</strong> Anonymous statistics such as page views, visit times, device types collected through Google Analytics</li>
              <li><strong>Technical Information:</strong> Browser type, operating system, IP address (anonymized)</li>
              <li><strong>Cookies:</strong> Used to save user preferences (theme, language settings, etc.)</li>
            </ul>
            <h3>1.2 User-Provided Information</h3>
            <ul>
              <li><strong>Tool Usage:</strong> Data you input into tools (JSON, timestamps, etc.) is processed locally in your browser and never uploaded to servers</li>
              <li><strong>History:</strong> Saved in browser local storage, not sent to servers</li>
            </ul>
          `
        },
        {
          title: '2. Information Usage',
          content: `
            <p>We use collected information to:</p>
            <ul>
              <li>Improve website functionality and user experience</li>
              <li>Analyze usage patterns and trends</li>
              <li>Save user preference settings (theme, language)</li>
              <li>Monitor website performance and errors</li>
            </ul>
            <p><strong>We will NOT:</strong></p>
            <ul>
              <li>❌ Sell your personal information</li>
              <li>❌ Share your data with third parties (except services mentioned below)</li>
              <li>❌ Track your personal identity</li>
            </ul>
          `
        },
        {
          title: '3. Third-Party Services',
          content: `
            <p>We use the following third-party services:</p>
            <h3>3.1 Google Analytics</h3>
            <ul>
              <li><strong>Purpose:</strong> Website traffic analysis</li>
              <li><strong>Data:</strong> Page views, user sources, device information</li>
              <li><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
              <li><strong>Opt-out:</strong> Install <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a></li>
            </ul>
            <h3>3.2 Google AdSense</h3>
            <ul>
              <li><strong>Purpose:</strong> Display advertisements on the website</li>
              <li><strong>Data:</strong> Google and its partners place and read cookies in your browser to serve interest-based advertising</li>
              <li><strong>Cookie Usage:</strong>
                <ul>
                  <li>Serve personalized ads</li>
                  <li>Measure advertising effectiveness</li>
                  <li>Prevent ad fraud</li>
                </ul>
              </li>
              <li><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
              <li><strong>Learn More:</strong> <a href="https://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener noreferrer">How Google uses data from partner sites</a></li>
              <li><strong>Manage Ad Preferences:</strong> Visit <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a> to customize your ad experience</li>
            </ul>
            <h3>3.3 Cloudflare</h3>
            <ul>
              <li><strong>Purpose:</strong> CDN acceleration and security</li>
              <li><strong>Privacy Policy:</strong> <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare Privacy Policy</a></li>
            </ul>
          `
        },
        {
          title: '4. Cookies and Local Storage',
          content: `
            <p>We use Cookies and browser local storage to:</p>
            <h3>4.1 Necessary Cookies</h3>
            <ul>
              <li><strong>User Preferences:</strong> Save theme settings (light/dark), language choice</li>
              <li><strong>History:</strong> Save your JSON input history (local only)</li>
            </ul>
            <h3>4.2 Analytics Cookies</h3>
            <ul>
              <li><strong>Google Analytics:</strong> Used to analyze website usage</li>
              <li><strong>Data Collection:</strong> Anonymous visit statistics</li>
            </ul>
            <h3>4.3 Advertising Cookies (Third-Party)</h3>
            <ul>
              <li><strong>Google AdSense:</strong> Google and its advertising partners use cookies to:
                <ul>
                  <li>Place and read cookies in your browser</li>
                  <li>Serve ads based on your past visits to this website and other websites</li>
                  <li>Measure the effectiveness of advertisements</li>
                </ul>
              </li>
              <li><strong>Personalized Advertising:</strong> These cookies enable Google and its partners to show you ads based on your visits to this site and/or other sites on the Internet</li>
            </ul>
            <h3>4.4 How to Control Cookies</h3>
            <ul>
              <li><strong>Browser Settings:</strong> You can disable cookies in your browser settings</li>
              <li><strong>Google Ads Settings:</strong> Visit <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a> to opt out of personalized advertising</li>
              <li><strong>Opt-out of Advertising Cookies:</strong> Visit <a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">aboutads.info</a> or <a href="http://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer">youronlinechoices.eu</a></li>
              <li><strong>Note:</strong> Some features may not work properly after disabling cookies (such as saving preferences)</li>
            </ul>
          `
        },
        {
          title: '5. Data Security',
          content: `
            <p>We take the following measures to protect your data:</p>
            <ul>
              <li><strong>HTTPS Encryption:</strong> All data transmission uses SSL/TLS encryption</li>
              <li><strong>Local Processing:</strong> Tool data (JSON, etc.) is completely processed locally in browser</li>
              <li><strong>No Sensitive Data Storage:</strong> Our servers do not store your tool usage data</li>
              <li><strong>Regular Updates:</strong> Keep software and dependencies updated</li>
            </ul>
          `
        },
        {
          title: '6. Your Rights',
          content: `
            <p>According to relevant laws and regulations, you have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> You can clear browser cookies and local storage to delete all local data</li>
              <li><strong>Opt-out:</strong> You can disable cookies or use ad blockers to opt out of tracking</li>
              <li><strong>Data Portability:</strong> Local data can be exported through browser developer tools</li>
            </ul>
          `
        },
        {
          title: '7. Children\'s Privacy',
          content: `
            <p>Our service is not directed to children under 13. We do not knowingly collect personal information from children. If you discover we have inadvertently collected children's information, please contact us to delete it.</p>
          `
        },
        {
          title: '8. Policy Changes',
          content: `
            <p>We may update this privacy policy from time to time. For major changes, we will:</p>
            <ul>
              <li>Update the "Last Updated" date at the top of this page</li>
              <li>Post a notice on the website</li>
            </ul>
            <p>We recommend checking this policy regularly for updates.</p>
          `
        },
        {
          title: '9. Contact Us',
          content: `
            <p>If you have any questions or suggestions about this privacy policy, please contact us through:</p>
            <ul>
              <li><strong>GitHub:</strong> <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">Submit an Issue</a></li>
              <li><strong>Website:</strong> <a href="/contact">Contact Page</a></li>
            </ul>
          `
        }
      ]
    }
  };

  const data = content[language];

  return (
    <div className="min-h-screen bg-[#faf9f6] dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-4">
            <Shield size={32} />
            <h1 className="text-4xl font-bold">{data.title}</h1>
          </div>
          <p className="text-lg text-indigo-100">{data.lastUpdated}</p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Link to="/" className="hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-1">
              <Home size={14} />
              {language === 'zh' ? '首页' : 'Home'}
            </Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">{data.title}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg dark:prose-invert max-w-none
          prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700
          prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
          prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
          prose-ul:my-4 prose-li:my-1
        ">
          {data.sections.map((section, index) => (
            <section key={index}>
              <h2>{section.title}</h2>
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </section>
          ))}
        </div>

        {/* Back Button */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </article>
    </div>
  );
}

export default PrivacyPolicy;
